/**
 * The grown-up's side of "I want to learn this word".
 *
 * Two jobs on one page, and the second is the one that makes the first safe.
 *
 * **Deciding.** A word the app has never heard of waits here. The dictionary
 * entry is shown with it, so the decision can be made without looking the
 * word up — a queue that costs a search each is a queue nobody works
 * through, and a queue nobody works through is a feature that quietly stops
 * being safe because the children learn to ask somewhere else.
 *
 * **Seeing what was stopped.** Blocked requests are shown too, greyed out and
 * below. Nothing is waiting on them, and that is exactly why they are easy to
 * leave out — but a child asking about a word the app refused is the one
 * thing worth knowing, and this is the only place it surfaces. Sam's question
 * was what happens when the filter fails; this is the answer for when it
 * works, and for when it fires on something harmless.
 *
 * A grown-up can overrule a block. It is their child and the list is a
 * blocklist, which means it is wrong sometimes — "knife" is on it, and a
 * child reading a cookery book has a perfectly good reason to want the word.
 */
import { useEffect, useState } from 'react'
import { api, type Child, type WordRequest } from '../api'
import { theme, TAP_MIN } from '../theme'
import { Button, Card } from './ui'
import { inputStyle } from './ui'
import { Trace, TraceRegion } from '../lib/trace'
import { fromServer, t } from '../lib/i18n'

export function WordRequests({ children }: { children: Child[] }) {
  const [rows, setRows] = useState<WordRequest[] | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const load = () => {
    api.wordRequestsForParent()
      .then(r => setRows(r.requests))
      .catch(() => setRows([]))
  }
  useEffect(load, [])

  const decide = async (id: string, yes: boolean) => {
    setBusy(id)
    try {
      if (yes) await api.approveWordRequest(id)
      else await api.declineWordRequest(id)
      load()
    } finally {
      setBusy(null)
    }
  }

  const waiting = (rows ?? []).filter(r => r.status === 'waiting')
  const blocked = (rows ?? []).filter(r => r.status === 'blocked')

  return (
    <TraceRegion id="T22" note="every ask is a row, answered or not">
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <p style={{ fontSize: 14, color: theme.faint, lineHeight: 1.6, margin: 0 }}>
        {t("Words your children asked to learn that Word Sprout did not know yet. Add the ones you are happy with; they join that child's words.")}
      </p>

      {rows === null ? (
        <Card><span style={{ color: theme.muted }}>{t('Looking…')}</span></Card>
      ) : null}

      {rows !== null && !waiting.length && !blocked.length ? (
        <Card>
          <span style={{ color: theme.muted, fontSize: 16 }}>
            {t('Nothing waiting. Words your children ask for will show up here.')}
          </span>
        </Card>
      ) : null}

      {waiting.map(r => (
        <Row key={r.request_id} request={r} busy={busy === r.request_id}
             onDecide={(yes) => void decide(r.request_id, yes)} />
      ))}

      {blocked.length ? (
        <>
          <h3 style={{ fontSize: 15, fontWeight: 800, color: theme.muted,
                       margin: '10px 0 0' }}>
            {t('Stopped before it reached you')}
          </h3>
          <p style={{ fontSize: 14, color: theme.faint, lineHeight: 1.6,
                      margin: 0 }}>
            {t('A check said no, either because the word is on the block list or because its dictionary entry is. Your child was told it is not one for this app and nothing else. You can overrule any of these.')}
          </p>
          {blocked.map(r => (
            <Row key={r.request_id} request={r} busy={busy === r.request_id}
                 blocked
                 onDecide={(yes) => void decide(r.request_id, yes)} />
          ))}
        </>
      ) : null}

      <AddOne children={children} onAdded={load} />
    </div>
    </TraceRegion>
  )
}


function Row({
  request, busy, blocked = false, onDecide,
}: {
  request: WordRequest
  busy: boolean
  blocked?: boolean
  onDecide: (yes: boolean) => void
}) {
  return (
    <Card style={blocked ? { background: theme.bg, borderStyle: 'dashed' } : undefined}>
      {/* The word on its own line, then one even line about it (Sam, 8 Oct:
          three sizes on one line did not line up). */}
      <div style={{ fontSize: 22, fontWeight: 800, color: theme.text, lineHeight: 1.2 }}>
        <Trace id="T22.asked">{request.asked}</Trace>
      </div>
      <div style={{ marginTop: 4, fontSize: 14, color: theme.muted }}>
        {t('Asked by')} <Trace id="T22.learner_id">{request.child_name}</Trace>
        {request.difficulty_band ? (
          <> · <Trace id="T22.difficulty_band">{t('about grade {grade}', { grade: request.difficulty_band })}</Trace></>
        ) : null}
      </div>

      {request.definition ? (
        <p style={{ margin: '8px 0 0', fontSize: 16, color: theme.text2,
                    lineHeight: 1.5 }}>
          <Trace id="T22.definition">{request.definition}</Trace>
        </p>
      ) : (
        <p style={{ margin: '8px 0 0', fontSize: 15, color: theme.faint }}>
          {t('No dictionary entry — it was stopped before we looked.')}
        </p>
      )}
      {request.example ? (
        <p style={{ margin: '6px 0 0', fontSize: 15, color: theme.muted,
                    fontStyle: 'italic', lineHeight: 1.5 }}>
          <Trace id="T22.example">{request.example}</Trace>
        </p>
      ) : null}
      {request.source ? (
        <div style={{ marginTop: 6, fontSize: 13, color: theme.fainter }}>
          {t('Meaning from')} <Trace id="T22.source">{request.source.split('+')[0]?.replace(/^./, (c) => c.toUpperCase())}</Trace>
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
        <Button tone="child" onClick={() => onDecide(true)} disabled={busy}>
          {blocked ? t('Allow it anyway') : t('Add it')}
        </Button>
        {!blocked ? (
          <Button tone="ghost" onClick={() => onDecide(false)} disabled={busy}>
            {t('Not this one')}
          </Button>
        ) : null}
      </div>
    </Card>
  )
}


/** A grown-up adding a word directly. No waiting, same checks. */
function AddOne({
  children, onAdded,
}: { children: Child[]; onAdded: () => void }) {
  const [who, setWho] = useState(children[0]?.learner_id ?? '')
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [said, setSaid] = useState<string | null>(null)

  if (!children.length) return null

  const send = async () => {
    if (!typed.trim() || !who || busy) return
    setBusy(true)
    setSaid(null)
    try {
      const made = await api.askForWord(who, typed.trim(), 'parent')
      setSaid(
        made.status === 'added'
          ? t('“{word}” added.', { word: made.asked })
          : t(made.note || 'That one is not available in this app.'))
      setTyped('')
      onAdded()
    } catch (err) {
      setSaid(fromServer(err, 'That did not work.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <div style={{ fontSize: 17, fontWeight: 800, color: theme.text,
                    marginBottom: 4 }}>
        {t('Add a word yourself')}
      </div>
      <p style={{ margin: '0 0 14px', fontSize: 14, color: theme.faint,
                  lineHeight: 1.6 }}>
        {t('Goes in straight away. The same checks still run — being the grown-up is not a reason to build a vocabulary app around weapons.')}
      </p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap',
                    alignItems: 'center' }}>
        {children.length > 1 ? (
          <select
            value={who}
            onChange={(e) => setWho(e.target.value)}
            style={{
              ...inputStyle, width: 'auto', minHeight: TAP_MIN,
              fontSize: 16, padding: '10px 12px',
            }}
          >
            {children.map(c => (
              <option key={c.learner_id} value={c.learner_id}>
                {c.display_name}
              </option>
            ))}
          </select>
        ) : null}
        <input
          style={{ ...inputStyle, flex: '1 1 160px', width: 'auto' }}
          value={typed}
          onChange={(e) => { setTyped(e.target.value); setSaid(null) }}
          onKeyDown={(e) => { if (e.key === 'Enter') void send() }}
          placeholder={t('One word')}
          autoComplete="off"
          maxLength={30}
        />
        <Button tone="quiet" onClick={() => void send()}
                disabled={!typed.trim() || busy}>
          {busy ? t('Looking…') : t('Add')}

        </Button>
      </div>

      {said ? (
        <p style={{ margin: '12px 0 0', fontSize: 15, color: theme.text2,
                    lineHeight: 1.5 }}>
          {said}
        </p>
      ) : null}
    </Card>
  )
}


/** How many need a decision, for the tab label. */
export function useWaitingCount(): number {
  const [n, setN] = useState(0)
  useEffect(() => {
    api.wordRequestsForParent()
      .then(r => setN(r.requests.filter(x => x.status === 'waiting').length))
      .catch(() => undefined)
  }, [])
  return n
}
